import { observable } from 'mobx';

import type { AdjustmentMeetingType } from './adjustment';

export type UserSettingResponse = {
  max_suggest_count: number;
  target_start_hour: number;
  target_end_hour: number;
  max_candidate_hours: number;
  default_event_place?: string;
  default_event_place_address?: string;
  suggest_count_in_day?: number;
  suggest_weekday?: string;
  calendar_checked?: string;
  share_html_with_head?: boolean;
  share_html_with_foot?: boolean;
  share_html_slashed_date?: boolean;
  suggest_holiday?: boolean;
  meeting_type?: AdjustmentMeetingType;
  share_use_html?: boolean;
};

export type UserSettingArgs = {
  maxSuggestCount: number;
  targetStartHour: number;
  targetEndHour: number;
  maxCandidateHours: number;
  defaultEventPlace?: string;
  defaultEventPlaceAddress?: string;
  suggestCountInDay: number;
  suggestWeekday?: string;
  calendarChecked?: string;
  shareHTMLWithHead?: boolean;
  shareHTMLWithFoot?: boolean;
  shareHTMLSlashedDate?: boolean;
  suggestHoliday?: boolean;
  meetingType?: AdjustmentMeetingType;
  shareUseHTML?: boolean;
};

export const convertUserSettingResponseToArgs = (response: UserSettingResponse) => {
  return {
    maxSuggestCount: response.max_suggest_count,
    targetStartHour: response.target_start_hour,
    targetEndHour: response.target_end_hour,
    maxCandidateHours: response.max_candidate_hours,
    defaultEventPlace: response.default_event_place,
    defaultEventPlaceAddress: response.default_event_place_address,
    suggestCountInDay: response.suggest_count_in_day || 1, // tableには保存せず毎回1にresetする
    suggestWeekday: response.suggest_weekday,
    calendarChecked: response.calendar_checked,
    shareHTMLWithHead: response.share_html_with_head,
    shareHTMLWithFoot: response.share_html_with_foot,
    shareHTMLSlashedDate: response.share_html_slashed_date,
    suggestHoliday: response.suggest_holiday,
    meetingType: response.meeting_type || 'none',
    shareUseHTML: response.share_use_html
  };
};

const DEFAULT_ARGS = {
  maxSuggestCount: 3,
  targetStartHour: 9,
  targetEndHour: 18,
  maxCandidateHours: 3,
  suggestCountInDay: 1,
  suggestWeekday: '0111110',
  suggestHoliday: true,
  meetingType: 'none'
} as const;

export default class UserSetting {
  @observable maxSuggestCount: number;
  @observable targetStartHour: number;
  @observable targetEndHour: number;
  @observable maxCandidateHours: number;
  @observable defaultEventPlace?: string;
  @observable defaultEventPlaceAddress?: string;
  @observable suggestCountInDay: number;
  @observable suggestWeekday?: string;
  @observable suggestHoliday: boolean;
  @observable calendarChecked?: string;
  @observable shareHTMLWithHead: boolean;
  @observable shareHTMLWithFoot: boolean;
  @observable shareHTMLSlashedDate: boolean;
  @observable meetingType: AdjustmentMeetingType;
  @observable shareUseHTML: boolean;

  constructor(args: UserSettingArgs) {
    this.maxSuggestCount = args.maxSuggestCount;
    this.targetStartHour = args.targetStartHour;
    this.targetEndHour = args.targetEndHour;
    this.maxCandidateHours = args.maxCandidateHours;
    this.defaultEventPlace = args.defaultEventPlace;
    this.defaultEventPlaceAddress = args.defaultEventPlaceAddress;
    this.suggestCountInDay = args.suggestCountInDay;
    this.suggestWeekday = args.suggestWeekday;
    this.calendarChecked = args.calendarChecked;
    this.shareHTMLWithHead = args.shareHTMLWithHead === true;
    this.shareHTMLWithFoot = args.shareHTMLWithFoot === true;
    this.shareHTMLSlashedDate = args.shareHTMLSlashedDate === true;
    this.suggestHoliday = args.suggestHoliday === true;
    this.meetingType = args.meetingType || 'none';
    this.shareUseHTML = args.shareUseHTML === true;
  }

  submitParams() {
    return {
      max_suggest_count: this.maxSuggestCount,
      target_start_hour: this.targetStartHour,
      target_end_hour: this.targetEndHour,
      max_candidate_hours: this.maxCandidateHours,
      default_event_place: this.defaultEventPlace,
      default_event_place_address: this.defaultEventPlaceAddress,
      suggest_count_in_day: this.suggestCountInDay,
      suggest_weekday: this.suggestWeekday,
      calendar_checked: this.calendarChecked,
      share_html_with_head: this.shareHTMLWithHead,
      share_html_with_foot: this.shareHTMLWithFoot,
      share_html_slashed_date: this.shareHTMLSlashedDate,
      suggest_holiday: this.suggestHoliday,
      meeting_type: this.meetingType,
      share_use_html: this.shareUseHTML
    };
  }

  static get defaultSetting() {
    return new UserSetting(DEFAULT_ARGS);
  }
}
